import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { RouteRef, SubRouteRef } from '@backstage/core-plugin-api';
import { useVersionedContext } from '@backstage/version-bridge';

type RouteFunc = (...args: readonly unknown[]) => string;

interface RoutingResolver {
  resolve(
    routeRef: RouteRef | SubRouteRef,
    context: { pathname: string },
  ): RouteFunc | undefined;
}

// Like useRouteRef, but returns a resolver function instead of resolving
// a single ref. Safe to call in loops — the hooks run once, not per item.
// Uses the same version-bridge routing context that useRouteRef uses internally.
export const useRouteRefResolver = () => {
  const { pathname } = useLocation();
  const versionedContext = useVersionedContext<{ 1: RoutingResolver }>(
    'routing-context',
  );
  const resolver = versionedContext?.atVersion(1);

  return useMemo(() => {
    if (!resolver) {
      throw new Error('Routing context is not available');
    }
    return (routeRef: RouteRef | SubRouteRef): RouteFunc => {
      const routeFunc = resolver.resolve(routeRef, { pathname });
      if (!routeFunc) {
        throw new Error(`No path for ${routeRef}`);
      }
      return routeFunc;
    };
  }, [resolver, pathname]);
};
